/**
 * Reusable pagination controls and "showing X–Y of N" meta.
 *
 * Pager: prev / page indicator / next, with optional fetching hint.
 * PageMeta: a single-line summary of the current slice.
 *
 * Both are presentational — the parent owns URL/search state and
 * computes `page`, `totalPages`, etc. from the API response.
 */

type PaginationProps = {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  isFetching?: boolean;
  ariaLabel?: string;
  /**
   * Customize the prev/next button copy. Defaults to "← Prev" / "Next →".
   * /history uses "← Newer" / "Older →" because newest-first reads better
   * as a chronological pair than as positional prev/next.
   */
  labels?: { prev?: string; next?: string };
  /**
   * Override the wrapper className. Default `"mt-5"` (bottom-of-list
   * placement). Pass e.g. `"mb-3"` when rendering the same control above
   * the list so the spacing reads as a top control.
   */
  className?: string;
};

const BTN_CLASS =
  "border border-ink-200 px-4 py-2 text-sm text-ink-700 hover:border-ink-700 hover:text-ink-900 transition-colors disabled:opacity-40 disabled:hover:border-ink-200 disabled:hover:text-ink-700 disabled:cursor-not-allowed";

export function Pagination({
  page,
  totalPages,
  onPrev,
  onNext,
  isFetching = false,
  ariaLabel = "Pagination",
  labels,
  className = "mt-5",
}: PaginationProps) {
  if (totalPages <= 1) return null;
  const atFirst = page <= 1;
  const atLast = page >= totalPages;
  const prevLabel = labels?.prev ?? "← Prev";
  const nextLabel = labels?.next ?? "Next →";

  return (
    <nav
      aria-label={ariaLabel}
      className={`flex items-center justify-between ${className}`}
    >
      <button
        type="button"
        onClick={onPrev}
        disabled={atFirst}
        className={BTN_CLASS}
      >
        {prevLabel}
      </button>
      <span
        className="text-xs text-ink-400 tabular-nums"
        aria-live="polite"
        aria-atomic="true"
      >
        Page {page} of {totalPages}
        {isFetching && page > 1 ? " · loading…" : ""}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={atLast}
        className={BTN_CLASS}
      >
        {nextLabel}
      </button>
    </nav>
  );
}

type PageMetaProps = {
  from: number;
  to: number;
  total: number;
  noun?: string;
};

export function PageMeta({ from, to, total, noun }: PageMetaProps) {
  return (
    <div className="flex items-baseline justify-between mb-3 text-xs text-ink-400 tabular-nums">
      <span>
        Showing {from}–{to} of {total}
        {noun ? ` ${noun}` : ""}
      </span>
    </div>
  );
}
